/**
 * Angebote – alles, was gerade läuft.
 *
 * Eine Liste, eine Menge, drei Filter, die nacheinander auf dasselbe Ergebnis
 * rechnen: Leiste (Schritt), Pillen (Phase und Person), Suche.
 *
 * Die Reiter „Laufend · Wiedervorlage · In Abwicklung" sind mit v14 entfallen
 * (17.2). Sie behaupteten drei getrennte Listen, wo es eine gibt: Eine
 * Wiedervorlage ist ein laufendes Angebot, das ruht – das sagt die Leiste
 * besser als ein Reiter. Und „In Abwicklung" war gar kein Angebot mehr,
 * sondern ein Auftrag; der steht im Menüpunkt Aufträge.
 *
 * Die Farben der Leiste sind die Rubrikfarben von „Mein Tag" (17.3): Dieselbe
 * Arbeit sieht überall gleich aus, auch wenn sich jemand seine Rubriken
 * umgefärbt hat.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { Nachrichtenknopf } from '../komponenten/ChatKontext';
import { KoraMaskenhinweise } from '../komponenten/KoraHinweise';
import { useKora, useMeta } from '../zustand';
import { Menueknopf } from '../komponenten/Shell';
import { LiegtBei } from '../komponenten/LiegtBei';
import { useRubrikton } from '../hilfen/rubrikton';
import {
  Gruppenkopf,
  Gruppieren,
  Listensuche,
  Ordnerzelle,
  Pille,
  Spaltenkopf,
  gewerkAus,
  naechsteSortierung,
  vergleich,
  type Sortierung,
} from '../komponenten/Liste';
import { StationenBahnen, type Bahn } from '../komponenten/StationenBahnen';
import type { BahnId } from '../hilfen/bahnen';
import { dat, eur } from '../hilfen/format';
import { kritischAb, liegestufe } from '../hilfen/liegezeit';
import { SummeModal } from '../modals/SummeModal';
import type { Angebot, Ton } from '../typen';
import './cockpit.css';

/**
 * Ruht der Vorgang?
 *
 * Zwei Wege führen auf Wiedervorlage, und beide zählen: der Status
 * „Wiedervorlage" mit Termin – und der Rückkehrtermin aus dem Rückmeldungs-
 * Dialog („weiter beobachten", wieder am 29.09.), bei dem der Status
 * „Gesendet" bleibt.
 *
 * Eine fällige Wiedervorlage ruht nicht mehr: Sie ist wieder ein laufender
 * Vorgang, sonst müsste man zwei Listen im Blick behalten, um zu wissen, was
 * heute dran ist.
 */
function ruht(a: Angebot): boolean {
  return a.laeuftNoch || (a.status === 'Wiedervorlage' && !a.wvFaellig);
}

/**
 * Der Schritt, in dem ein Angebot steht – die Segmente der Leiste (17.2).
 *
 * Die ersten vier Fälle liest die Funktion nicht selbst, sondern fragt die
 * Zuständigkeitsmatrix: `liegtBei.kachel` ist die Rubrik, in der der Vorgang
 * auf „Mein Tag" liegt. So steht dieselbe Regel nicht zweimal da und kann
 * nicht auseinanderlaufen.
 *
 * Die eigene Entscheidung ist nur die Trennung zwischen „Beim Kunden" und
 * „Nachfassen": Für die Matrix liegt beides beim Versender, in der Liste ist
 * es der Unterschied zwischen Warten und Arbeit.
 */
function schrittVon(a: Angebot): string {
  if (ruht(a)) return 'wv';
  const kachel = a.liegtBei?.kachel;
  if (kachel === 'kalkulieren') return 'kalk';
  if (kachel === 'pruefen') return 'pruef';
  if (kachel === 'versenden') return 'ankuend';
  if (a.beimKunden) return a.nachfass.anrufFaellig || a.wvFaellig ? 'nachf' : 'kunde';
  return 'kalk';
}

/**
 * Die Stationen des Angebotswegs (STATIONEN-LISTEN.md, 4).
 *
 * `kurz` steht in der Statusspalte der Liste, `lang` auf der Kachel – dort
 * ist Platz für den ganzen Namen, und gekürzt sagt er nichts.
 *
 * „Rückfrage des Kunden" bleibt in der Produktion leer: Der Status existiert
 * in Kora nicht, er kam aus dem alten Board. Die Kachel steht trotzdem da –
 * eine Station, die verschwindet, sobald sie leer ist, macht die Übersicht
 * bei jedem Laden anders lang.
 */
const SCHRITTE: { key: string; kurz: string; lang: string; rubrik: string; ruht?: boolean }[] = [
  { key: 'kalk', kurz: 'Kalkulieren', lang: 'In Kalkulation', rubrik: 'kalkulieren' },
  { key: 'pruef', kurz: 'Prüfen', lang: 'Prüfung / Freigabe', rubrik: 'pruefen' },
  { key: 'ankuend', kurz: 'Versenden', lang: 'Ankündigen & Versenden', rubrik: 'versenden' },
  { key: 'kunde', kurz: 'Beim Kunden', lang: 'Beim Kunden · wartet', rubrik: 'wartet' },
  { key: 'rueck', kurz: 'Rückfrage', lang: 'Rückfrage des Kunden', rubrik: 'wartet' },
  { key: 'nachf', kurz: 'Nachfassen', lang: 'Nachfassen fällig', rubrik: 'nachfassen' },
  { key: 'wv', kurz: 'Wiedervorlage', lang: 'Wiedervorlage · ruht', rubrik: 'wiedervorlage', ruht: true },
];

/**
 * Wer bei welchem Schritt dran ist (STATIONEN-BAHNEN.md 1).
 *
 * Eine feste Tabelle, nicht je Vorgang entschieden: „Beim Kunden" liegt beim
 * Kunden, egal wer den Vorgang kalkuliert hat. Die Wiedervorlage steht in
 * keiner Bahn – sie ruht und bekommt rechts eine eigene Kachel (2.8).
 */
const PARTEI_VON: Record<string, BahnId> = {
  kalk: 'eh',
  pruef: 'eh',
  ankuend: 'eh',
  nachf: 'eh',
  kunde: 'kunde',
  rueck: 'kunde',
};

const BAHNEN_ANGEBOTE: { id: BahnId; name: string }[] = [
  { id: 'eh', name: 'Eichenherz' },
  { id: 'kunde', name: 'Kunde' },
];


/**
 * „Beim Kunden" ist keine Rubrik, sondern Warten (v14, 17.2).
 *
 * Es steht in keiner Kachel von „Mein Tag" – dort liegt nichts auf einem
 * Tisch, solange der Kunde überlegt. Deshalb trägt der Schritt keinen
 * Prozesston, sondern Neutral: ein Punkt, der sagt „hier ruht es", ohne
 * eine Zuständigkeit zu behaupten.
 */
const WARTET: Ton = {
  id: 'wartet',
  label: 'Wartet',
  akz: 'var(--eh-text-stumm)',
  akzTxt: 'var(--eh-text-3)',
  dunkel: 'var(--eh-text-3)',
  tint: 'var(--eh-neutral-flaeche)',
};

const GRUPPEN = [
  { key: 'dring', label: 'Dringlichkeit' },
  { key: 'gewerk', label: 'Gewerk' },
  { key: 'wer', label: 'Liegt bei' },
  { key: 'keine', label: 'Keine Gruppierung' },
];

export function Cockpit() {
  const { nutzer } = useKora();
  const meta = useMeta();
  const navigate = useNavigate();
  const ton = useRubrikton();
  const { toastZeigen } = useKora();
  const [suchParameter, setzeSuchParameter] = useSearchParams();

  const [schrittFilter, setSchrittFilter] = useState<string | null>(null);
  /* Bahnfilter: die Partei, auf die gefiltert ist (STATIONEN-BAHNEN.md 3). */
  const [parteiFilter, setParteiFilter] = useState<BahnId | null>(null);
  const [person, setPerson] = useState('');
  const [suche, setSuche] = useState('');
  const [sort, setSort] = useState<Sortierung>({ key: 'liegezeit', richtung: 'ab' });
  const [gruppierung, setGruppierung] = useState('dring');
  const [zu, setZu] = useState<Record<string, boolean>>({});
  /** „+ Summe" öffnet dasselbe Feld wie auf der Startseite – nicht ein zweites. */
  const [summeFuer, setSummeFuer] = useState<Angebot | null>(null);

  const abfrage = useQuery({
    queryKey: ['angebote'],
    queryFn: () => api.hole<Angebot[]>('/angebote'),
  });

  const meinName = nutzer?.name ?? '';
  const warnAb = meta.liegezeit.orangeBis;
  const krit = kritischAb(warnAb);

  /** Die eine Menge, aus der alle Zahlen kommen: die offenen Angebote. */
  const alle = useMemo(
    () => (abfrage.data ?? []).filter((a) => a.offen).map((a) => ({ a, schritt: schrittVon(a) })),
    [abfrage.data],
  );

  // Sprung aus dem Team-Überblick: auf die Person filtern.
  useEffect(() => {
    const wer = suchParameter.get('versender');
    if (!wer) return;
    setPerson(wer);
    setzeSuchParameter({}, { replace: true });
  }, [suchParameter, setzeSuchParameter]);

  /** Bei wem der Vorgang liegt – dieselbe Regel wie auf „Mein Tag". */
  const zustaendig = (a: Angebot) => a.liegtBei?.person || a.versender || a.kalkulant;

  /**
   * Die Dringlichkeit einer Zeile – Gruppen und Vorgabe-Sortierung (17.6).
   *
   * 0 fällig · 1 über 21 Tage beim Kunden · 2 14–20 · 3 darunter · 4 noch
   * nicht beim Kunden. „Nachfassen fällig" sticht die Liegezeit: Was heute
   * drankommt, gehört nach oben, egal wie lange es schon liegt.
   */
  const dringlichkeit = (a: Angebot, schritt: string): number => {
    if (schritt === 'nachf') return 0;
    if (!a.beimKunden) return 4;
    return a.alter >= krit ? 1 : a.alter >= warnAb ? 2 : 3;
  };

  const DRING_KOPF: { titel: string; zusatz: string; punkt: string }[] = [
    { titel: 'Nachfassen fällig', zusatz: 'jetzt dran', punkt: 'var(--eh-krit)' },
    { titel: `Über ${krit} Tage beim Kunden`, zusatz: 'Nachfassen steht bald an', punkt: 'var(--eh-krit)' },
    { titel: `${warnAb} bis ${krit - 1} Tage`, zusatz: '', punkt: 'var(--eh-warn)' },
    { titel: `Unter ${warnAb} Tagen`, zusatz: 'noch frisch', punkt: 'var(--eh-rahmen-schalter)' },
    { titel: 'Noch nicht beim Kunden', zusatz: 'Kalkulation, Prüfung, Ankündigung', punkt: 'var(--eh-rahmen-schalter)' },
  ];

  const SPALTEN: Record<
    string,
    {
      label: string;
      wert: (x: { a: Angebot; schritt: string }) => string | number | null;
      leer?: (x: { a: Angebot; schritt: string }) => boolean;
      klasse?: string;
      rechts?: boolean;
    }
  > = {
    nr: { label: 'Angebot', wert: (x) => x.a.name.toLowerCase() },
    status: {
      label: 'Status',
      wert: (x) => SCHRITTE.findIndex((s) => s.key === x.schritt),
      klasse: 'cp-c-status',
    },
    liegezeit: { label: 'Liegezeit', wert: (x) => x.a.alter, klasse: 'cp-c-liege' },
    summe: {
      label: 'Summe',
      wert: (x) => x.a.summe,
      leer: (x) => x.a.summe === null,
      klasse: 'cp-c-summe',
      rechts: true,
    },
    schritt: {
      label: 'Nächster Schritt',
      wert: (x) => dringlichkeit(x.a, x.schritt),
      klasse: 'cp-c-schritt',
    },
    wer: {
      label: 'Liegt bei',
      wert: (x) => x.a.liegtBei?.personName ?? zustaendig(x.a) ?? null,
      leer: (x) => !zustaendig(x.a),
    },
  };

  /** Was heute Arbeit ist – ohne die ruhenden Wiedervorlagen. */
  const laufend = useMemo(() => alle.filter((x) => x.schritt !== 'wv'), [alle]);
  const ruhend = alle.length - laufend.length;

  const gefiltert = useMemo(() => {
    const worte = suche.trim().toLowerCase().split(/\s+/).filter(Boolean);

    return alle
      .filter(
        (x) =>
          /*
           * Ruhende Vorgänge stehen nicht in der Liste, bis man das
           * Segment „Wiedervorlage" anklickt (17.2). Sie kommen an ihrem
           * Datum von selbst zurück; in der Arbeitsliste wären sie vierzehn
           * Zeilen, bei denen heute nichts zu tun ist.
           */
          (x.schritt !== 'wv' || schrittFilter === 'wv') &&
          (!schrittFilter || x.schritt === schrittFilter) &&
          (!parteiFilter || PARTEI_VON[x.schritt] === parteiFilter) &&
          (!person || zustaendig(x.a) === person) &&
          (!worte.length ||
            worte.every((w) =>
              [x.a.name, x.a.kunde.name, x.a.kunde.ort, x.a.kategorie, x.a.prefix, x.a.status]
                .join(' ')
                .toLowerCase()
                .includes(w),
            )),
      )
      .slice()
      .sort((p, q) => vergleich(p, q, sort, SPALTEN));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alle, schrittFilter, parteiFilter, person, suche, sort]);

  /**
   * Personen-Pillen – nur für die, die in dieser Ansicht etwas haben.
   * Der eigene Name steht vorn und ist gekennzeichnet.
   */
  const personen = useMemo(() => {
    const zaehler = new Map<string, number>();
    for (const { a } of laufend) {
      const wer = zustaendig(a);
      if (wer) zaehler.set(wer, (zaehler.get(wer) ?? 0) + 1);
    }
    return [...zaehler.entries()]
      .map(([name, anzahl]) => ({ name, anzahl, ich: name === meinName }))
      .sort((x, y) => Number(y.ich) - Number(x.ich) || y.anzahl - x.anzahl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laufend, meinName]);

  const gruppen = useMemo(() => {
    const schluesselVon = (x: { a: Angebot; schritt: string }) =>
      gruppierung === 'dring'
        ? String(dringlichkeit(x.a, x.schritt))
        : gruppierung === 'gewerk'
          ? gewerkAus(x.a.name) || '—'
          : gruppierung === 'wer'
            ? zustaendig(x.a) || '—'
            : 'alle';

    const eimer = new Map<string, { a: Angebot; schritt: string }[]>();
    for (const x of gefiltert) {
      const k = schluesselVon(x);
      if (!eimer.has(k)) eimer.set(k, []);
      eimer.get(k)!.push(x);
    }
    const keys = [...eimer.keys()];
    if (gruppierung === 'dring') keys.sort((a, b) => Number(a) - Number(b));
    else if (gruppierung !== 'keine') keys.sort((a, b) => eimer.get(b)!.length - eimer.get(a)!.length);

    return keys.map((k) => {
      const drin = eimer.get(k)!;
      const kopf = DRING_KOPF[Number(k)];
      const titel =
        gruppierung === 'dring'
          ? (kopf?.titel ?? '')
          : gruppierung === 'gewerk'
            ? `${k} · ${meta.kategorien.find((x) => x.prefix === k)?.label ?? 'Sonstige'}`
            : gruppierung === 'wer'
              ? k === '—'
                ? 'Niemand zuständig'
                : k
              : '';
      const mit = drin.filter((x) => x.a.summe !== null);
      const ohne = drin.length - mit.length;
      const summe = mit.reduce((s, x) => s + (x.a.summe ?? 0), 0);
      return {
        key: k,
        titel,
        punkt: gruppierung === 'dring' ? kopf?.punkt : undefined,
        zusatz:
          `${drin.length} ${drin.length === 1 ? 'Angebot' : 'Angebote'}` +
          (gruppierung === 'dring' && kopf?.zusatz ? ` · ${kopf.zusatz}` : ''),
        /* Ehrlich zur Lücke: „93.300 €" allein sähe vollständig aus. */
        rechts: mit.length ? eur(summe) + (ohne ? ` · ${ohne} ohne Summe` : '') : `${ohne} ohne Summe`,
        zeilen: drin,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gefiltert, gruppierung, meta.kategorien, warnAb]);

  /* Der Ton einer Phase – „Beim Kunden" wartet und trägt keinen Prozesston. */

  const anzahlIn = (key: string) => alle.filter((x) => x.schritt === key).length;
  const neuIn = (key: string) => alle.filter((x) => x.schritt === key && x.a.neu).length;
  const summeIn = (keys: string[]) =>
    alle.filter((x) => keys.includes(x.schritt)).reduce((s, x) => s + (x.a.summe ?? 0), 0);

  /*
   * Die Bahnen (STATIONEN-BAHNEN.md 2).
   *
   * Jede Spalte ist ein Schritt und steht in jeder Bahn; die Kachel liegt
   * nur bei der Partei, die dran ist. Die Wiedervorlage steht außerhalb –
   * sie ruht und gehört niemandem auf den Tisch.
   */
  const spaltenSchritte = SCHRITTE.filter((st) => !st.ruht);
  const bahnen: Bahn[] = BAHNEN_ANGEBOTE.map((b) => {
    const eigeneKeys = spaltenSchritte.filter((st) => PARTEI_VON[st.key] === b.id).map((st) => st.key);
    const summe = summeIn(eigeneKeys);
    return {
      id: b.id,
      name: b.name,
      summe: summe > 0 ? eur(summe) : 'ohne Summe',
      summeSchwach: summe === 0,
      schritte: spaltenSchritte.map((st) => ({
        key: st.key,
        label: st.lang,
        anzahl: anzahlIn(st.key),
        neu: neuIn(st.key),
        eigen: PARTEI_VON[st.key] === b.id,
      })),
    };
  });

  const wiedervorlage = { key: 'wv', anzahl: anzahlIn('wv'), neu: neuIn('wv') };

  return (
    <div className="cp">
      <div className="cp-kopf">
        <div className="eh-kopf-links">
          <Menueknopf />
          <div>
            <div className="cp-kicker">Pipeline</div>
            <h1 className="cp-titel">Angebote</h1>
            <div className="cp-untertitel">
              {laufend.length} laufende {laufend.length === 1 ? 'Vorgang' : 'Vorgänge'}
              {ruhend > 0 ? ` · ${ruhend} ruhen` : ''} · Stand {meta.heuteText}
            </div>
          </div>
        </div>
        <div className="cp-kopfaktionen eh-kopf-rechts">
          {/*
            Das Nachrichten-Symbol steht als erstes, linkes Element der
            rechten Kopfgruppe – auf jeder Maske gleich (v14, 7.2).
          */}
          <Nachrichtenknopf />
          <button type="button" className="cp-neu" onClick={() => navigate('/neu')}>
            + Neues Angebot
          </button>
        </div>
      </div>

      {abfrage.isLoading && <div className="eh-karte">Die Angebote werden geladen …</div>}
      {abfrage.isError && <div className="eh-karte">Die Angebote konnten nicht geladen werden.</div>}

      {abfrage.data && (
        <>
          <StationenBahnen
            titel="Wo die Angebote stehen"
            bahnen={bahnen}
            wiedervorlage={wiedervorlage}
            schritt={schrittFilter}
            partei={parteiFilter}
            hinweis={
              schrittFilter
                ? `gefiltert auf „${SCHRITTE.find((x) => x.key === schrittFilter)?.lang}" · nochmal klicken hebt auf`
                : parteiFilter
                  ? `gefiltert auf „${BAHNEN_ANGEBOTE.find((b) => b.id === parteiFilter)?.name}" · nochmal klicken hebt auf`
                  : 'Klick auf Schritt oder Bahn filtert · Farbe je Bahn über den Punkt'
            }
            onSchritt={(k) => {
              setSchrittFilter((alt) => (alt === k ? null : k));
              setParteiFilter(null);
            }}
            onPartei={(id) => {
              setParteiFilter((alt) => (alt === id ? null : id));
              setSchrittFilter(null);
            }}
          />

          {/*
            Der Personenfilter steht seit v15 unter der Übersicht und nicht
            mehr in ihr (3): Er filtert zusätzlich, nicht statt der Bahnen.
          */}
          <div className="st-personen">
            <span className="st-personen-label">Liegt bei</span>
            <Pille label="Alle" zahl={laufend.length} aktiv={person === ''} onKlick={() => setPerson('')} />
            {personen.map((p) => (
              <Pille
                key={p.name}
                label={p.ich ? `${p.name} (du)` : p.name}
                zahl={p.anzahl}
                aktiv={person === p.name}
                onKlick={() => setPerson(person === p.name ? '' : p.name)}
              />
            ))}
          </div>

          <div className="li-werkzeuge">
            <Listensuche wert={suche} onAendern={setSuche} />
            <div className="li-werkzeuge-rechts">
              <span className="li-sorthinweis">
                {sort.key
                  ? `sortiert nach ${SPALTEN[sort.key]!.label} ${sort.richtung === 'auf' ? '▲' : '▼'}`
                  : 'ohne Sortierung'}
              </span>
              <Gruppieren
                optionen={GRUPPEN}
                wert={gruppierung}
                onWaehlen={(k) => {
                  setGruppierung(k);
                  setZu({});
                }}
              />
            </div>
          </div>

          <div className="li-tabelle">
            <div className="cp-zeile li-kopfzeile">
              {Object.entries(SPALTEN).map(([key, s]) => (
                <Spaltenkopf
                  key={key}
                  spalte={key}
                  label={s.label}
                  klasse={s.klasse}
                  rechts={s.rechts}
                  sort={sort}
                  onSort={(sp) => setSort((alt) => naechsteSortierung(alt, sp))}
                />
              ))}
              <span />
            </div>

            {gruppen.map((g) => (
              <div key={g.key} className="cp-gruppe">
                {gruppierung !== 'keine' && (
                  <Gruppenkopf
                    label={g.titel}
                    punkt={g.punkt}
                    zusatz={g.zusatz}
                    rechts={g.rechts}
                    offen={!zu[g.key]}
                    onKlappen={() => setZu((x) => ({ ...x, [g.key]: !x[g.key] }))}
                  />
                )}
                {!zu[g.key] &&
                  g.zeilen.map(({ a, schritt }) => (
                    <Zeile
                      key={a.id}
                      a={a}
                      schritt={schritt}
                      akz={
                        SCHRITTE.find((s) => s.key === schritt)?.rubrik === 'wartet'
                          ? WARTET.akz
                          : ton(SCHRITTE.find((s) => s.key === schritt)?.rubrik ?? 'kalkulieren').akz
                      }
                      orangeBis={warnAb}
                      onOeffnen={() => navigate(`/angebot/${a.id}`, { state: { herkunft: 'cockpit' } })}
                      onSumme={setSummeFuer}
                      onToast={toastZeigen}
                    />
                  ))}
              </div>
            ))}

            <div className="li-fuss">
              {gefiltert.length === 0
                ? 'Kein Angebot passt zu Filter und Suche.'
                : `${gefiltert.length} von ${schrittFilter === 'wv' ? ruhend : laufend.length} ${schrittFilter === 'wv' ? 'ruhenden' : 'laufenden'} Angeboten` +
                  (gruppierung !== 'keine'
                    ? ` · gruppiert nach ${GRUPPEN.find((x) => x.key === gruppierung)!.label}`
                    : '') +
                  (suche.trim() ? ` · Suche „${suche.trim()}"` : '')}
            </div>
          </div>
        </>
      )}

      {summeFuer && (
        <SummeModal
          angebot={summeFuer}
          onSchliessen={() => setSummeFuer(null)}
          onFertig={() => setSummeFuer(null)}
        />
      )}
      {/* KORA steht unter dem Inhalt der Maske, nicht darüber (v11, 10.2 Regel 6). */}
      <KoraMaskenhinweise maske="angebote" label="Angebote" />
    </div>
  );
}

/* ------------------------------------------------------------------------- */

/**
 * Die Liegezeit als Status-Chip.
 *
 * Nur bei versendeten Angeboten farbig: Ein Angebot in Kalkulation ist nach
 * zwanzig Tagen nicht dramatisch, ein versendetes schon. Farbe als
 * Eskalation, nicht als Dekoration – und aus derselben Funktion wie auf
 * „Mein Tag", damit derselbe Vorgang nicht in zwei Masken verschieden
 * dringend aussieht.
 */
function liegeTon(a: Angebot, orangeBis: number): string {
  if (!a.beimKunden) return 'neutral';
  const stufe = liegestufe(a.alter, orangeBis);
  return stufe === 'rot' ? 'krit' : stufe === 'bernstein' ? 'warn' : 'neutral';
}

/**
 * Was als Nächstes ansteht – im Klartext.
 *
 * Die Liste sagt, dass etwas dran ist und seit bzw. ab wann. Wie nachgefasst
 * wird – Mail oder Anruf – entscheidet der Vertrieb im Vorgang.
 */
function naechsterSchritt(a: Angebot, schritt: string): { text: string; unten: string; ton: '' | 'krit' | 'warn' } {
  if (schritt === 'wv') {
    const wann = a.wiedervorlage ?? a.faellig;
    return { text: 'Wiedervorlage', unten: wann ? `wieder am ${dat(wann)}` : 'ruht', ton: '' };
  }
  if (schritt === 'nachf') {
    if (a.wvFaellig && a.wiedervorlage) {
      return { text: 'Wiedervorlage fällig', unten: `vereinbart für ${dat(a.wiedervorlage)}`, ton: 'krit' };
    }
    return { text: 'Nachfassen fällig', unten: `seit ${dat(a.nachfass.anrufAb)}`, ton: 'krit' };
  }
  if (schritt === 'kunde') {
    return { text: 'liegt beim Kunden', unten: `Nachfassen ab ${dat(a.nachfass.anrufAb)}`, ton: '' };
  }
  if (schritt === 'ankuend') {
    return { text: 'Kunden anrufen, dann versenden', unten: 'noch nicht beim Kunden', ton: '' };
  }
  if (schritt === 'pruef') {
    return { text: 'Prüfung abschließen', unten: 'noch nicht beim Kunden', ton: '' };
  }
  return { text: 'Kalkulation läuft', unten: 'noch nicht beim Kunden', ton: '' };
}

function Zeile({
  a,
  schritt,
  akz,
  orangeBis,
  onOeffnen,
  onSumme,
  onToast,
}: {
  a: Angebot;
  schritt: string;
  akz: string;
  orangeBis: number;
  onOeffnen: () => void;
  onSumme: (a: Angebot) => void;
  onToast: (text: string) => void;
}) {
  const naechster = naechsterSchritt(a, schritt);
  const stand = SCHRITTE.find((s) => s.key === schritt);

  return (
    <div className="cp-zeile" style={{ borderLeftColor: akz }}>
      {/* Kunde und Ort stehen unter der Nummer – dort ist Breite. */}
      <button type="button" className="li-vorgang" title={`${a.name} öffnen`} onClick={onOeffnen}>
        <span className="li-nummer li-kurz">{a.name}</span>
        <span className="li-kunde li-kurz">
          {a.kunde.name}
          {a.kunde.ort ? ` · ${a.kunde.ort}` : ''}
        </span>
      </button>

      {/*
        Punkt statt Chip. Dreizehn identische graue „Gesendet"-Chips
        untereinander sind Rauschen: Der Chip zieht Aufmerksamkeit, ohne zu
        unterscheiden.
      */}
      {/*
        Die Kurzform, nicht die Langform: Die Spalte ist 120 px breit
        (17.10), und „Nachfassen fällig" endete darin als „Nachfassen
        fäl…". Ein abgeschnittenes Wort ist schlechter als ein kürzeres;
        der ganze Satz steht im Tooltip und in „Nächster Schritt".
      */}
      <span className="cp-c-status cp-statuszelle">
        <span className="cp-statuspunkt" style={{ background: akz }} />
        <span className="li-kurz" title={`${stand?.lang ?? a.status} · ${a.alterText} in diesem Stand`}>
          {stand?.kurz ?? a.status}
        </span>
      </span>

      <span className="cp-c-liege cp-liegezeit">
        <span className={`eh-status-chip ${liegeTon(a, orangeBis)}`}>{a.alterText}</span>
        <span className="cp-seit">seit {a.statusDatumText}</span>
      </span>

      <span className="cp-c-summe cp-betrag" onClick={(e) => e.stopPropagation()}>
        {a.summe === null ? (
          <button
            type="button"
            className="cp-summe-fehlt"
            title="Angebotssumme netto nachtragen"
            onClick={() => onSumme(a)}
          >
            + Summe
          </button>
        ) : (
          <span title={`${a.summeText} netto`}>{a.summeText}</span>
        )}
      </span>

      <span className="cp-c-schritt cp-schritt">
        <span className={`li-kurz cp-schritt-text ${naechster.ton}`}>{naechster.text}</span>
        <span className="li-kurz cp-schritt-unten">{naechster.unten}</span>
      </span>

      {/* Avatar und voller Name, ohne Rolle (17.4) – in jeder Liste gleich. */}
      <LiegtBei lb={a.liegtBei} spur />

      <Ordnerzelle art="angebot" ordnerName={a.ordnerName} onToast={onToast} />
    </div>
  );
}
